const _excluded = ["isOpen", "children", "onRequestClose", "allowCloseOnOutsideClick", "animation", "placement", "shape", "isModal", "hasBackdrop"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import * as React from "react";
import { usePreventScroll } from "react-aria";
import Cross from "@bolteu/kalep-react-icons/dist/Cross";
import { IconButton } from "../IconButton";
import { cx } from "../../helpers/utils/cx";
import { filterDataAttributes } from "../../helpers/utils/props";
export const ModalContext = /*#__PURE__*/React.createContext({
  isOpen: false,
  isOverflowVisible: false
});
export function BaseModal(_ref) {
  let {
      isOpen,
      children,
      onRequestClose,
      allowCloseOnOutsideClick = true,
      animation,
      placement,
      shape = "shadow-xl rounded-lg",
      isModal = true,
      hasBackdrop = true
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const dialogRef = React.useRef(null);
  const dataAttributes = filterDataAttributes(props);

  // Keeps track of actual <dialog> element open state, so we can provide it in context
  const [isElementOpen, setIsElementOpen] = React.useState(false);

  // Helper to close <dialog> with an animation
  const animatedCloseModal = React.useCallback(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      dialogRef.current?.close();
      setIsElementOpen(false);
    } else if (dialogRef.current && elementHasAnimation(dialogRef.current)) {
      dialogRef.current.setAttribute("data-closing", "");
      dialogRef.current.addEventListener("animationend", () => {
        dialogRef.current?.removeAttribute("data-closing");
        dialogRef.current?.close();
        setIsElementOpen(false);
      }, {
        once: true
      });
    } else {
      dialogRef.current?.close();
      setIsElementOpen(false);
    }
  }, [dialogRef]);

  // Opening and closing the dialog according to isOpen prop changes.
  React.useEffect(() => {
    const isDialogOpen = dialogRef.current?.open;
    if (isOpen && !isDialogOpen) {
      if (isModal) {
        dialogRef.current?.showModal?.();
      } else {
        dialogRef.current?.show?.();
      }
      setIsElementOpen(true);
    }
    if (!isOpen && isDialogOpen) {
      animatedCloseModal();
    }
  }, [isOpen, isModal, dialogRef, animatedCloseModal]);

  // When platform effects for closing dialog kick in (e.g Esc key), then catch
  // the cancel event on the dialog and block closing (via preventDefault).
  // Instead, request closing from parent.
  // This gives parent control over the dialog.
  const cancelHandler = React.useCallback(event => {
    event.preventDefault();

    // request closing from parent
    onRequestClose();
  }, [onRequestClose]);

  // On Escape keypress event check if event's target is input, textfield or select field.
  // In that case blur the target field and prevent modal closing.
  React.useEffect(() => {
    const {
      current
    } = dialogRef;
    const handleEscape = event => {
      const target = event.target;
      if (event.key === "Escape" && (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.getAttribute("role") === "listbox")) {
        event.preventDefault();
        target?.blur();
      }
    };
    current?.addEventListener("keydown", handleEscape);
    return () => current?.removeEventListener("keydown", handleEscape);
  }, [dialogRef]);

  // Request closing when clicking outside of modal.
  // The <dialog> element covers the whole screen. The visible part is wrapped
  // in a div. Thus, when a click event on the dialog is on the dialog
  // element itself (nodeName === DIALOG), it means the click was on the backdrop.
  // https://youtu.be/4prVdA7_6u0?t=901
  const outsideClickHandler = React.useCallback(event => {
    if (allowCloseOnOutsideClick) {
      if (event.target instanceof HTMLElement && event.target.nodeName === "DIALOG") {
        // check that we're not in the middle of a selection
        if (!window.getSelection()?.toString()) {
          onRequestClose();
        }
      }
    }
  }, [allowCloseOnOutsideClick, onRequestClose]);
  usePreventScroll({
    isDisabled: !isElementOpen
  });
  const modalContextValue = React.useMemo(() => ({
    isOpen: isElementOpen
  }), [isElementOpen]);
  return /*#__PURE__*/React.createElement(ModalContext.Provider, {
    value: modalContextValue
  }, /*#__PURE__*/React.createElement("dialog", _extends({
    ref: dialogRef,
    onClick: outsideClickHandler,
    onCancel: cancelHandler,
    className: cx("p-0 bg-special-nulled border-0", shape, hasBackdrop ? "backdrop:animate-fade-in [&[data-closing]::backdrop]:animate-fade-out backdrop:bg-special-scrim" : "backdrop:hidden",
    // default to fade-in animation, allow overriding.
    // when applying animations with movement, use motion-safe:
    animation ?? "animate-fade-in [&[data-closing]]:animate-fade-out", placement, "overflow-visible"),
    "aria-labelledby": props["aria-labelledby"],
    "aria-label": props["aria-label"],
    role: props.role
  }, dataAttributes, {
    "data-kalep-comp": "basemodal"
  }), /*#__PURE__*/React.createElement("div", null, children)));
}
function ModalClose(_ref2) {
  let {
    onClose,
    autoFocus
  } = _ref2;
  return /*#__PURE__*/React.createElement(IconButton, {
    overrideClassName: "-mt-1 -me-1",
    icon: /*#__PURE__*/React.createElement(Cross, null),
    size: "sm",
    onClick: onClose,
    "aria-label": "Close",
    autoFocus: autoFocus
  });
}
function ModalHeader(_ref3) {
  let {
    title,
    titleId,
    children
  } = _ref3;
  return /*#__PURE__*/React.createElement("div", {
    className: cx("flex gap-6 px-6 pt-6 pb-4 justify-between items-center"),
    "data-kalep-comp": "basemodal/header"
  }, title && /*#__PURE__*/React.createElement("span", {
    id: titleId,
    className: cx("bolt-font-heading-s-accent text-primary overflow-hidden break-words")
  }, title), children);
}

// Mostly here because it's exactly the same between Drawer and Dialog.
function ModalContent(_ref4) {
  let {
    children
  } = _ref4;
  const contentRef = React.useRef(null);
  const {
    isOpen
  } = React.useContext(ModalContext);
  React.useEffect(() => {
    if (isOpen) {
      const contentElement = contentRef.current;
      if ((contentElement?.scrollHeight || 0) > (contentElement?.clientHeight || 0)) {
        contentElement?.classList.add("border-y");
        contentElement?.classList.add("border-y-separator");
      }
    }
  }, [isOpen, contentRef]);
  return /*#__PURE__*/React.createElement("section", {
    ref: contentRef,
    className: cx("py-2 px-6 border-solid border-0", "flex-grow", "text-base font-normal text-primary", "overflow-y-auto"),
    "data-kalep-comp": "basemodal/content"
  }, children);
}
function ModalFooter(_ref5) {
  let {
    children
  } = _ref5;
  return /*#__PURE__*/React.createElement("footer", {
    className: cx("pt-4 pb-6 px-6"),
    "data-kalep-comp": "basemodal/footer"
  }, children);
}
function elementHasAnimation(el) {
  const styleDeclaration = window.getComputedStyle(el);
  const animationDuration = parseFloat(styleDeclaration.getPropertyValue("animation-duration"));
  const transitionDuration = parseFloat(styleDeclaration.getPropertyValue("transition-duration"));
  return animationDuration > 0 || transitionDuration > 0;
}
BaseModal.Close = ModalClose;
BaseModal.Header = ModalHeader;
BaseModal.Content = ModalContent;
BaseModal.Footer = ModalFooter;